import { Controller } from '@hotwired/stimulus';
import Swal from 'sweetalert2';

export default class extends Controller<HTMLFormElement> {

    static values = {
        title: String,
        text: String,
        confirmButtonText: String,
        cancelButtonText: String,
        url: String,
        method: { type: String, default: 'POST' },
        sectionUrl: String,
        sectionTarget: String,
        requiredWord: String,
        inputLabel: String,
        inputPlaceholder: String,
        inputValidationMessage: String,
    };

    declare readonly titleValue: string;
    declare readonly textValue: string;
    declare readonly confirmButtonTextValue: string;
    declare readonly cancelButtonTextValue: string;
    declare readonly urlValue: string;
    declare readonly methodValue: string;
    declare readonly sectionUrlValue: string;
    declare readonly sectionTargetValue: string;
    declare readonly requiredWordValue: string;
    declare readonly inputLabelValue: string;
    declare readonly inputPlaceholderValue: string;
    declare readonly inputValidationMessageValue: string;
    declare readonly hasUrlValue: boolean;
    declare readonly hasSectionUrlValue: boolean;
    declare readonly hasSectionTargetValue: boolean;
    declare readonly hasRequiredWordValue: boolean;

    connect(): void {

        this.element.addEventListener(
            'submit',
            this.handleSubmit
        );
    }

    disconnect(): void {

        this.element.removeEventListener(
            'submit',
            this.handleSubmit
        );
    }

    private handleSubmit = async (
        event: Event
    ): Promise<void> => {

        // Enviar el <form> real que disparó el submit. El controller puede estar
        // sobre el propio form o sobre un contenedor padre (donde this.element es
        // un div sin .submit()), así que nos apoyamos en event.target.
        const form = (event.target as HTMLElement | null)?.closest('form');

        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        event.preventDefault();

        // Evita que un confirm de un contenedor padre lo procese de nuevo.
        event.stopImmediatePropagation();

        const result = await this.confirm();

        if (result.isConfirmed) {
            this.appendConfirmationWord(form, result.value);
            form.submit();
        }
    };

    async ask(event: Event): Promise<void> {
        event.preventDefault();

        const trigger = event.currentTarget as HTMLButtonElement | null;
        const url = trigger?.dataset.confirmUrlParam ?? (this.hasUrlValue ? this.urlValue : '');
        const method = trigger?.dataset.confirmMethodParam ?? this.methodValue ?? 'POST';
        const sectionUrl = trigger?.dataset.confirmSectionUrlParam ?? (this.hasSectionUrlValue ? this.sectionUrlValue : '');
        const sectionTarget = trigger?.dataset.confirmSectionTargetParam ?? (this.hasSectionTargetValue ? this.sectionTargetValue : '');

        const result = await this.confirm();
        if (!result.isConfirmed) return;

        if (!url) return;

        trigger?.setAttribute('disabled', 'disabled');

        try {
            const response = await fetch(url, {
                method,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) {
                throw new Error(`Unexpected response status ${response.status}`);
            }

            if (sectionUrl && sectionTarget) {
                await this.reloadSection(sectionUrl, sectionTarget);
            }
        } catch (error) {
            console.error('[confirm] action error:', error);
            trigger?.removeAttribute('disabled');
            await Swal.fire({
                title: 'Error',
                text: 'No se pudo completar la acción.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    }

    private confirm(): Promise<Awaited<ReturnType<typeof Swal.fire>>> {
        const requiredWord = this.hasRequiredWordValue ? this.requiredWordValue : '';

        return Swal.fire({
            title:
                this.titleValue ||
                '¿Estás seguro?',

            text:
                this.textValue ||
                'Esta acción no se puede deshacer.',

            icon: 'warning',

            showCancelButton: true,

            confirmButtonText:
                this.confirmButtonTextValue ||
                'Sí, eliminar',

            cancelButtonText:
                this.cancelButtonTextValue ||
                'Cancelar',

            reverseButtons: true,

            focusCancel: true,

            input: requiredWord ? 'text' : undefined,

            inputLabel:
                requiredWord
                    ? (this.inputLabelValue || `Escribí "${requiredWord}" para continuar.`)
                    : undefined,

            inputPlaceholder:
                requiredWord
                    ? (this.inputPlaceholderValue || requiredWord)
                    : undefined,

            inputValidator:
                requiredWord
                    ? (value: string | null) => {
                        if ((value || '').trim().toLowerCase() !== requiredWord.toLowerCase()) {
                            return this.inputValidationMessageValue || `Escribí "${requiredWord}" para continuar.`;
                        }

                        return null;
                    }
                    : undefined,
        });
    }

    private appendConfirmationWord(form: HTMLFormElement, value: unknown): void {
        if (!this.hasRequiredWordValue) {
            return;
        }

        let input = form.querySelector<HTMLInputElement>('input[name="_confirmation_word"]');

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = '_confirmation_word';
            form.appendChild(input);
        }

        input.value = String(value || '');
    }

    private async reloadSection(url: string, selector: string): Promise<void> {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await response.text();
        const target = document.querySelector(selector);
        if (target) target.outerHTML = html;
    }
}
